import React, { useEffect, useRef, useState, useCallback } from 'react';
import './CustomVideoPlayer.css';

const CustomVideoPlayer = ({ videoUrl, onVideoComplete }) => {
  const playerContainerId = 'youtube-player-container';
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTopBar, setShowTopBar] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [hasTriggeredCompletion, setHasTriggeredCompletion] = useState(false);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const topBarTimeoutRef = useRef(null);

  const formatTime = seconds => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Extrair video ID do YouTube
  const getYouTubeVideoId = url => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const onPlayerReady = useCallback(event => {
    console.log('Player pronto!');
    setDuration(event.target.getDuration());
    event.target.setVolume(100);
    // Mostrar barra de baixo quando vídeo carrega
    setIsPlaying(false);
  }, []);

  const onPlayerStateChange = useCallback(
    event => {
      const playingStates = [window.YT?.PlayerState?.PLAYING, window.YT?.PlayerState?.BUFFERING];
      const isNowPlaying = playingStates.includes(event.data);

      // Atualizar estado imediatamente
      setIsPlaying(isNowPlaying);

      // Atualizar tempo atual
      if (playerRef.current && playerRef.current.getCurrentTime) {
        setCurrentTime(playerRef.current.getCurrentTime());
      }

      if (isNowPlaying) {
        // Mostrar barra do topo por 3 segundos quando começa a tocar
        setShowTopBar(true);

        // Limpar timeout anterior se existir
        if (topBarTimeoutRef.current) {
          clearTimeout(topBarTimeoutRef.current);
        }

        // Esconder a barra após 3 segundos
        topBarTimeoutRef.current = setTimeout(() => {
          setShowTopBar(false);
        }, 3000);

        // Limpar intervalo anterior
        if (timeIntervalRef.current) {
          clearInterval(timeIntervalRef.current);
        }

        // Atualizar tempo a cada 100ms enquanto toca
        timeIntervalRef.current = setInterval(() => {
          if (playerRef.current && playerRef.current.getCurrentTime) {
            const currentTimeUpdate = playerRef.current.getCurrentTime();
            const durationUpdate = playerRef.current.getDuration();
            setCurrentTime(currentTimeUpdate);

            // Verificar se faltam 2 segundos para acabar o vídeo
            if (durationUpdate > 0) {
              const remainingTime = durationUpdate - currentTimeUpdate;
              if (remainingTime <= 2 && onVideoComplete && !hasTriggeredCompletion) {
                setHasTriggeredCompletion(true);
                onVideoComplete();
              }
            }
          }
        }, 100);
      } else {
        // Mostrar barra quando pausado
        setShowTopBar(true);

        // Limpar timeout quando pausado
        if (topBarTimeoutRef.current) {
          clearTimeout(topBarTimeoutRef.current);
        }

        // Parar a atualização quando pausado
        if (timeIntervalRef.current) {
          clearInterval(timeIntervalRef.current);
        }
      }
    },
    [onVideoComplete, hasTriggeredCompletion]
  );

  // Carregar YouTube IFrame API e inicializar player
  useEffect(() => {
    const initializePlayer = () => {
      const videoId = getYouTubeVideoId(videoUrl);
      if (!videoId || !window.YT || !window.YT.Player) return;

      try {
        if (playerRef.current && playerRef.current.destroy) {
          playerRef.current.destroy();
          playerRef.current = null;
        }

        // Limpar estados
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);
        setShowTopBar(false);
        setHasTriggeredCompletion(false);

        playerRef.current = new window.YT.Player(playerContainerId, {
          height: '100%',
          width: '100%',
          videoId,
          playerVars: {
            controls: 0,
            autoplay: 0,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
            iv_load_policy: 3,
            disablekb: 0,
            fs: 0,
          },
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange,
          },
        });

        // Forçar dimensões após criação
        setTimeout(() => {
          const iframeElement = document.querySelector(`#${playerContainerId} iframe`);
          const containerDiv = document.querySelector(`#${playerContainerId}`);

          if (iframeElement) {
            iframeElement.style.width = '100%';
            iframeElement.style.height = '100%';
            iframeElement.style.padding = '0';
            iframeElement.style.margin = '0';
            iframeElement.style.border = 'none';
            iframeElement.style.display = 'block';
            iframeElement.style.overflow = 'hidden';
          }

          if (containerDiv) {
            containerDiv.style.width = '100%';
            containerDiv.style.height = '100%';
            containerDiv.style.padding = '0';
            containerDiv.style.margin = '0';
            containerDiv.style.overflow = 'hidden';
            containerDiv.style.display = 'block';
          }

          if (iframeElement && iframeElement.parentElement) {
            iframeElement.parentElement.style.width = '100%';
            iframeElement.parentElement.style.height = '100%';
            iframeElement.parentElement.style.padding = '0';
            iframeElement.parentElement.style.margin = '0';
            iframeElement.parentElement.style.overflow = 'hidden';
          }
        }, 500);
      } catch (e) {
        console.error('Erro ao criar player:', e);
      }
    };

    // Se a API já está carregada
    if (window.YT && window.YT.Player) {
      initializePlayer();
    } else if (!window.YT) {
      // Carregar a API
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        console.log('YouTube IFrame API carregada!');
        initializePlayer();
      };
    }

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
      if (topBarTimeoutRef.current) {
        clearTimeout(topBarTimeoutRef.current);
      }
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
      }
    };
  }, [videoUrl, onPlayerReady, onPlayerStateChange]);

  const handlePlayPause = () => {
    if (!playerRef.current) return;

    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  const handleProgressChange = e => {
    const newTime = (e.target.value / 100) * duration;
    if (playerRef.current && playerRef.current.seekTo) {
      playerRef.current.seekTo(newTime, true);
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = e => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (playerRef.current && playerRef.current.setVolume) {
      playerRef.current.setVolume(newVolume * 100);
    }
  };

  const handlePlaybackRateChange = rate => {
    setPlaybackRate(rate);
    if (playerRef.current && playerRef.current.setPlaybackRate) {
      playerRef.current.setPlaybackRate(rate);
    }
    setShowSpeedMenu(false);
  };

  const handleFullscreen = () => {
    if (containerRef.current) {
      if (!isFullscreen) {
        if (containerRef.current.requestFullscreen) {
          containerRef.current.requestFullscreen().catch(err => {
            console.error('Erro ao ativar fullscreen:', err);
          });
        } else if (containerRef.current.webkitRequestFullscreen) {
          containerRef.current.webkitRequestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        setIsFullscreen(false);
      }
    }
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={`custom-video-player-container ${!isPlaying ? 'is-paused' : ''}`}
      ref={containerRef}
    >
      {/* Container do YouTube Player */}
      <div id={playerContainerId} className="youtube-player-embed" />

      {/* Overlay para capturar cliques no vídeo */}
      <div className="video-overlay" onClick={handlePlayPause} />

      {/* Barra de controle no topo - apenas fundo preto */}
      <div className={`custom-controls-bar-top ${!isPlaying || showTopBar ? 'visible' : ''}`}></div>

      {/* Controles customizados no fundo */}
      <div className="custom-controls-bar">
        {/* Barra de progresso */}
        <div className="progress-container">
          <input
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={handleProgressChange}
            className="progress-slider"
          />
        </div>

        {/* Botões de controle */}
        <div className="controls-bottom">
          <button
            className="control-btn"
            onClick={handlePlayPause}
            title={isPlaying ? 'Pausar' : 'Reproduzir'}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>

          <span className="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <div className="controls-right-container">
            <div className="volume-control">
              <button className="control-btn" title="Volume">
                🔊
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="volume-slider"
              />
            </div>

            <div className="speed-control">
              <button
                className="speed-btn"
                title="Velocidade"
                onClick={() => setShowSpeedMenu(!showSpeedMenu)}
              >
                {playbackRate}x
              </button>
              <div className="speed-menu">
                {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map(rate => (
                  <button
                    key={rate}
                    className={`speed-option ${playbackRate === rate ? 'active' : ''}`}
                    onClick={() => handlePlaybackRateChange(rate)}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            </div>

            <button
              className="control-btn"
              onClick={handleFullscreen}
              title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
            >
              ⛶
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomVideoPlayer;
